import React, { useContext } from 'react';
import { Animated, StyleSheet } from 'react-native';
import { View } from 'react-native';
import Stack from '../../../v2BaseComponents/Stack';
import { consumerAppDesktopTabConfig, professionalAppTabConfig } from '../tabConfig';
import appNavigationNames, {
    TabNavigationParamList,
    useActiveTabActiveScreen,
    useNestedNavigation,
    useTabDefaultScreenNavigation,
    useTabNavigation,
    useTabRouteActiveProfile,
} from '../../../../utils/appNavigationNames';
import { DrawerElementProvider } from '../../../shared/MaybeSideNavLayout';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { getIntl } from '../../../../utils/intl';
import { useRoute } from '@react-navigation/native';
import NavigationItem from './NavigationItem';
import ProfileNavigationItems, { ProfileNavigationItem } from './ProfileNavigationItems';
import { useSignOutAndResetApp } from '../../../../hooks/useSignOutAndResetApp';
import Icon from '../../../v2BaseComponents/Icon';
import { colors, zIndex } from '../../../../utils/theme';
import { getSpacing } from '../../../../utils/spacing';
import useMedicalProfiles from '../../../../hooks/api/useMedicalProfiles';
import { useGetIsUserProfile } from '../../../../hooks/useUserHasOwnMedicalProfile';
import { useAppType } from '../../../../hooks/useAppType';
import { ConsumerAppGuard } from '../../../shared/AppTypeGuards';
import Divider from '../../../v2BaseComponents/Divider';
import { hexToRgbA } from '../../../../utils/hexToRGBA';
import { SideDrawerCollapsedStateContext } from '../../../shared/SideDrawerCollapsedStateProvider';
import { useDrawerWidthAnimation } from './useDrawerWidthAnimation';
import { PRScrollView } from '../../../shared/Scroll';

const intl = getIntl('Drawer', {
    headerImageAlt: 'Header Primary Record Logo',
    support: 'Support',
    signOut: 'Sign Out',
    collapse: 'Collapse',
});

const styles = StyleSheet.create({
    logo: {
        width: '100%',
        height: 24,
    },
    shadow: {
        boxShadow: '0px 3px 4.65px rgba(0, 0, 0, 0.27)',
        elevation: 6,
    } as any,
});

export type DrawerNavigationOwnProps = {};

export type DrawerNavigationProps = DrawerNavigationOwnProps;

const StackNavigator = createNativeStackNavigator();

const Drawer = () => {
    const isProApp = useAppType() === 'professional';
    const { isCollapsed, setIsCollapsed } = useContext(SideDrawerCollapsedStateContext);
    const widthAnimation = useDrawerWidthAnimation(isCollapsed);
    const tabDefaultScreenNavigation = useTabDefaultScreenNavigation();
    const { nestedAccountNavigation } = useNestedNavigation();
    const tabNavigator = useTabNavigation();
    const route = useRoute();
    const activeTabAndScreen = useActiveTabActiveScreen();
    const tabRouteActiveProfile = useTabRouteActiveProfile();
    const { signOutAndResetApp, isResetting } = useSignOutAndResetApp();
    const appType = useAppType();
    const tabConfig = (appType === 'professional' ? professionalAppTabConfig : consumerAppDesktopTabConfig).filter(
        (_) => !_.omitFromSideNav,
    );
    const nonAccountTabs = tabConfig.filter((_) => _.name !== 'account');
    const accountTab = tabConfig.filter((_) => _.name === 'account')[0];
    const { isLoadingProfiles, medicalProfiles } = useMedicalProfiles();
    const getIsUserProfile = useGetIsUserProfile();

    const shouldBeActive = (tabName: keyof TabNavigationParamList) => {
        if (tabName === 'profiles' && route.name === 'profiles') {
            return !tabRouteActiveProfile.profileId;
        } else {
            return tabName === route.name;
        }
    };

    const userProfile = (medicalProfiles || []).find((profile) => getIsUserProfile(profile));

    return (
        <Animated.View
            style={[
                {
                    height: '100%',
                    backgroundColor: colors[isProApp ? 'Pro-Drawer-Background' : 'Drawer-Background'],
                    zIndex: zIndex.drawer,
                },
                styles.shadow,
                { width: widthAnimation.current, overflow: 'visible' },
            ]}
        >
            <Stack
                style={{ width: '100%', height: '100%', justifyContent: 'space-between', overflow: 'visible' }}
                vertical
            >
                <Stack
                    space={getSpacing(2)}
                    style={{
                        width: '100%',
                        height: '100%',
                        overflow: 'visible',
                    }}
                    vertical
                >
                    <Stack
                        space={getSpacing(0.125)}
                        style={{
                            height: '100%',
                            overflow: 'visible',
                            width: '100%',
                            flex: 1,
                        }}
                        vertical
                    >
                        <PRScrollView style={{ width: '100%', flex: 1 }}>
                            <Stack
                                space={12}
                                style={{
                                    marginTop: getSpacing(2),
                                    width: '100%',
                                }}
                                vertical
                            >
                                <Stack space={4} vertical style={{ width: '100%' }}>
                                    {nonAccountTabs.map((tab) => {
                                        const isActive = shouldBeActive(tab.name);
                                        return (
                                            <NavigationItem
                                                key={tab.name}
                                                isActive={isActive}
                                                icon={<Icon name={tab.iconName} size="drawer-icon" color="White" />}
                                                onPress={() => {
                                                    // if the tab is active, we want to navigate to the root, else navigate to where the tab was left off at
                                                    tab.name === route.name
                                                        ? tabDefaultScreenNavigation(tab.name)
                                                        : tabNavigator.navigate(tab.name as any);
                                                }}
                                                testID={`navigation-tab-${tab.name}`}
                                                display={tab.display}
                                                isCollapsed={isCollapsed}
                                            />
                                        );
                                    })}
                                    <ConsumerAppGuard>
                                        {userProfile && !isLoadingProfiles ? (
                                            <ProfileNavigationItem profile={userProfile} isCollapsed={isCollapsed} />
                                        ) : null}
                                    </ConsumerAppGuard>
                                </Stack>
                                <ConsumerAppGuard>
                                    {() => <ProfileNavigationItems isCollapsed={isCollapsed} />}
                                </ConsumerAppGuard>
                            </Stack>
                        </PRScrollView>
                    </Stack>
                    <Stack
                        space={4}
                        style={{
                            width: '100%',
                            paddingBottom: getSpacing(1),
                        }}
                        vertical
                    >
                        <NavigationItem
                            isActive={
                                activeTabAndScreen?.activeTab === 'account' &&
                                activeTabAndScreen?.activeScreen !== 'support'
                            }
                            icon={<Icon name={'UserCircle'} size="drawer-icon" color="White" />}
                            onPress={() => {
                                // if the tab is active, we want to navigate to the root, else navigate to where the tab was left off at
                                route.name === 'account'
                                    ? tabDefaultScreenNavigation(accountTab.name)
                                    : tabNavigator.navigate(accountTab.name as any);
                            }}
                            testID={`navigation-tab-${accountTab.name}`}
                            display={accountTab.display}
                            isCollapsed={isCollapsed}
                        />
                        <NavigationItem
                            isActive={
                                activeTabAndScreen?.activeTab === 'account' &&
                                activeTabAndScreen?.activeScreen === 'support'
                            }
                            icon={<Icon name="HelpCircle" size="drawer-icon" color="White" />}
                            onPress={() => {
                                nestedAccountNavigation('support', undefined);
                            }}
                            testID={`navigation-tab-help`}
                            display={intl('support')}
                            isCollapsed={isCollapsed}
                        />
                        <NavigationItem
                            isActive={false}
                            icon={<Icon name="LogOut" size="drawer-icon" color="White" />}
                            onPress={signOutAndResetApp}
                            testID={`navigation-tab-signOut`}
                            display={intl('signOut')}
                            isCollapsed={isCollapsed}
                            isDisabled={isResetting}
                        />
                        <Divider
                            horizontal
                            style={{
                                height: 2,
                                marginTop: 10,
                                backgroundColor: hexToRgbA(
                                    colors[isProApp ? 'Pro-Drawer-Item-Active' : 'Drawer-Item-Active'],
                                    0.1,
                                ),
                            }}
                        />
                        <Stack vertical style={{ height: 55 }} fullWidth justify="center">
                            <NavigationItem
                                isActive={false}
                                icon={
                                    <Icon
                                        name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'}
                                        size="drawer-icon"
                                        color="White"
                                    />
                                }
                                onPress={() => setIsCollapsed(!isCollapsed)}
                                testID={`navigation-tab-collapse`}
                                display={intl('collapse')}
                                isCollapsed={isCollapsed}
                            />
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </Animated.View>
    );
};

const DrawerNavigation: React.FC<DrawerNavigationProps> = () => {
    const appType = useAppType();
    const tabConfig = appType === 'professional' ? professionalAppTabConfig : consumerAppDesktopTabConfig;
    return (
        <DrawerElementProvider drawerElement={<Drawer />}>
            <View testID="drawer-navigator-test-id" style={{ width: '100%', height: '100%' }}>
                <StackNavigator.Navigator initialRouteName={appNavigationNames.tabs.profiles}>
                    {tabConfig.map((tab) => (
                        <StackNavigator.Screen
                            key={tab.name}
                            name={tab.name}
                            component={tab.component}
                            options={{
                                headerShown: false,
                            }}
                        />
                    ))}
                </StackNavigator.Navigator>
            </View>
        </DrawerElementProvider>
    );
};

export default React.memo(DrawerNavigation);
